import { useCallback, useEffect, useState } from "react";
import { Plus, TrendingUp, TrendingDown, Wallet, ChevronLeft, ChevronRight } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { api, formatTL, currentMonthKey, shiftMonth, monthLabel, MONTH_NAMES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import TransactionModal from "../components/TransactionModal";
import TransactionItem from "../components/TransactionItem";
import RatesStrip from "../components/RatesStrip";
import BudgetStatus from "../components/BudgetStatus";
import InvitesCard from "../components/InvitesCard";

export default function Dashboard() {
  const { user } = useAuth();
  const [month, setMonth] = useState(currentMonthKey());
  const [summary, setSummary] = useState({ income: 0, expense: 0, balance: 0 });
  const [monthly, setMonthly] = useState([]);
  const [recent, setRecent] = useState([]);
  const [categories, setCategories] = useState({ income: [], expense: [] });
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    const [s, m, t] = await Promise.all([
      api.get(`/summary?month=${month}`),
      api.get("/reports/monthly?months=6"),
      api.get(`/transactions?month=${month}&limit=5`),
    ]);
    setSummary(s.data);
    setMonthly(m.data);
    setRecent(t.data);
  }, [month]);

  useEffect(() => { load(); }, [load]);
  const loadCategories = useCallback(() => api.get("/categories").then((r) => setCategories(r.data)), []);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  const chartData = monthly.map((m) => ({ ...m, net: m.income - m.expense, name: MONTH_NAMES[parseInt(m.month.split("-")[1], 10) - 1].slice(0, 3) }));

  return (
    <div className="space-y-6 fade-in-up" data-testid="dashboard-page">
      <InvitesCard />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Merhaba, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="text-muted-foreground text-sm mt-1">İşte {monthLabel(month)} özeti · <span className="text-primary">{user?.account?.name}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-card border border-border rounded-full">
            <button data-testid="month-prev-button" onClick={() => setMonth(shiftMonth(month, -1))}
              className="p-2 hover:text-primary transition-colors"><ChevronLeft className="w-4 h-4" /></button>
            <span data-testid="current-month-label" className="text-sm font-medium px-2 whitespace-nowrap">{monthLabel(month)}</span>
            <button data-testid="month-next-button" onClick={() => setMonth(shiftMonth(month, 1))}
              className="p-2 hover:text-primary transition-colors"><ChevronRight className="w-4 h-4" /></button>
          </div>
          {user?.permissions?.add && (
            <Button data-testid="add-transaction-button" onClick={() => setModalOpen(true)} className="rounded-full font-semibold">
              <Plus className="w-4 h-4 mr-1" /> Yeni İşlem
            </Button>
          )}
        </div>
      </div>

      <RatesStrip />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        <div data-testid="summary-income-card" className="bg-card border border-border rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
            <p className="text-sm text-muted-foreground">Toplam Gelir</p>
          </div>
          <p className="font-heading text-2xl md:text-3xl font-bold text-emerald-400">{formatTL(summary.income)}</p>
        </div>
        <div data-testid="summary-expense-card" className="bg-card border border-border rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-red-400" />
            </div>
            <p className="text-sm text-muted-foreground">Toplam Gider</p>
          </div>
          <p className="font-heading text-2xl md:text-3xl font-bold text-red-400">{formatTL(summary.expense)}</p>
        </div>
        <div data-testid="summary-balance-card" className="bg-card border border-primary/40 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">Net Bakiye</p>
          </div>
          <p className={`font-heading text-2xl md:text-3xl font-bold ${summary.balance >= 0 ? "text-primary" : "text-red-400"}`}>
            {formatTL(summary.balance)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-card border border-border rounded-2xl p-6">
          <h2 className="font-heading text-lg font-semibold mb-4">Son 6 Ay</h2>
          <div className="h-64" data-testid="monthly-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} barGap={4}>
                <XAxis dataKey="name" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  cursor={{ fill: "rgba(51,65,85,0.3)" }}
                  contentStyle={{ background: "#1E293B", border: "1px solid #334155", borderRadius: 12, color: "#F8FAFC" }}
                  formatter={(v, n) => [formatTL(v), n === "income" ? "Gelir" : n === "expense" ? "Gider" : "Net"]}
                />
                <Bar dataKey="income" fill="#10B981" radius={[6, 6, 0, 0]} />
                <Bar dataKey="expense" fill="#EF4444" radius={[6, 6, 0, 0]} />
                <Line dataKey="net" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3, fill: "#F59E0B" }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-6">
          <h2 className="font-heading text-lg font-semibold mb-4">Son İşlemler</h2>
          <div className="space-y-2" data-testid="recent-transactions-list">
            {recent.length === 0 && (
              <p data-testid="no-transactions-message" className="text-sm text-muted-foreground py-8 text-center">
                Bu ay henüz işlem yok. "Yeni İşlem" ile başlayın.
              </p>
            )}
            {recent.map((tx) => (
              <TransactionItem key={tx.id} tx={tx} meta={categories.meta} compact />
            ))}
          </div>
        </div>
      </div>

      <BudgetStatus month={month} refreshKey={summary} />

      <TransactionModal open={modalOpen} onOpenChange={setModalOpen} categories={categories} editing={null} onSaved={load} onCategoriesChanged={loadCategories} />
    </div>
  );
}
